import React, { useRef, useEffect, useCallback } from "react";

const CanvasPersonalizado = ({
  baseHue = 180,
  backgroundColor = "#fdfdfdff",
}) => {
  const canvasContainerRef = useRef(null);
  const animationRef = useRef(null);

  // Constantes matemáticas
  const maxDensity = 0.8;
  const PI = Math.PI;
  const HALF_PI = 0.5 * PI;
  const TAU = 2 * PI;
  const TO_RAD = PI / 180;

  const rand = (n) => n * Math.random();

  const fadeInOut = (t, m) => {
    let hm = 0.5 * m;
    return Math.abs(((t + hm) % m) - hm) / hm;
  };
  const dist = (x1, y1, x2, y2) =>
    Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));


  // Configuración de las tuberías
  const pipeCount = 30;
  const pipePropCount = 8;
  const pipePropsLength = pipeCount * pipePropCount;
  const turnCount = 8;
  const turnAmount = (360 / turnCount) * TO_RAD;
  const turnChanceRange = 58;
  const baseSpeed = 0.5;
  const rangeSpeed = 1;
  const baseTTL = 100;
  const rangeTTL = 300;
  const baseWidth = 2;
  const rangeWidth = 4;
  const rangeHue = 60;

  // Referencias para el estado del canvas
  const densityRef = useRef(0);
  const canvasRef = useRef({ a: null, b: null });
  const ctxRef = useRef({ a: null, b: null });
  const centerRef = useRef([]);
  const tickRef = useRef(0);
  const pipePropsRef = useRef(null);

  // Inicializar las tuberías
  const initPipes = useCallback(() => {
    pipePropsRef.current = new Float32Array(pipePropsLength);
    let i;
    for (i = 0; i < pipePropsLength; i += pipePropCount) {
      initPipe(i);
    }
  }, [pipePropsLength, pipePropCount]);

  const initPipe = useCallback(
    (i) => {
      const x = rand(canvasRef.current.a.width);
      const y = centerRef.current[1];
      const direction = Math.round(rand(1)) ? HALF_PI : TAU - HALF_PI;
      const speed = baseSpeed + rand(rangeSpeed);
      const life = 0;
      const ttl = baseTTL + rand(rangeTTL);
      const width = baseWidth + rand(rangeWidth);
      const hue = baseHue + rand(rangeHue);

      pipePropsRef.current.set(
        [x, y, direction, speed, life, ttl, width, hue],
        i
      );
    },
    [
      baseHue,
      baseSpeed,
      baseTTL,
      baseWidth,
      rangeHue,
      rangeSpeed,
      rangeTTL,
      rangeWidth,
    ]
  );

  const updatePipes = useCallback(() => {
    tickRef.current++;
    let i;
    for (i = 0; i < pipePropsLength; i += pipePropCount) {
      updatePipe(i);
    }
  }, [pipePropsLength, pipePropCount]);

  const updatePipe = useCallback(
    (i) => {
      const pipeProps = pipePropsRef.current;
      let i2 = 1 + i,
        i3 = 2 + i,
        i4 = 3 + i,
        i5 = 4 + i,
        i6 = 5 + i,
        i7 = 6 + i,
        i8 = 7 + i;
      let x, y, direction, speed, life, ttl, width, hue, turnChance, turnBias;

      x = pipeProps[i];
      y = pipeProps[i2];
      direction = pipeProps[i3];
      speed = pipeProps[i4];
      life = pipeProps[i5];
      ttl = pipeProps[i6];
      width = pipeProps[i7];
      hue = pipeProps[i8];

      drawPipe(x, y, life, ttl, width, hue);

      life++;
      x += Math.cos(direction) * speed;
      y += Math.sin(direction) * speed;
      turnChance =
        !(tickRef.current % Math.round(rand(turnChanceRange))) &&
        (!(Math.round(x) % 6) || !(Math.round(y) % 6));
      turnBias = Math.round(rand(1)) ? -1 : 1;
      direction += turnChance ? turnAmount * turnBias : 0;

      pipeProps[i] = x;
      pipeProps[i2] = y;
      pipeProps[i3] = direction;
      pipeProps[i5] = life;

      checkBounds(x, y);
      if (life > ttl) initPipe(i);

      // Calcular densidad (añadir esta parte al final de la función)
      if (tickRef.current % 60 === 0) {
        // Verificar cada 60 frames
        calculateDensity();
      }
    },
    [initPipe, turnAmount, turnChanceRange]
  );
  const drawPipe = useCallback((x, y, life, ttl, width, hue) => {
    ctxRef.current.a.save();
    ctxRef.current.a.strokeStyle = `hsla(${hue},75%,50%,${
      fadeInOut(life, ttl) * 0.125
    })`;
    ctxRef.current.a.beginPath();
    ctxRef.current.a.arc(x, y, width, 0, TAU);
    ctxRef.current.a.stroke();
    ctxRef.current.a.closePath();
    ctxRef.current.a.restore();
  }, []);

  const checkBounds = useCallback((x, y) => {
    if (x > canvasRef.current.a.width) x = 0;
    if (x < 0) x = canvasRef.current.a.width;
    if (y > canvasRef.current.a.height) y = 0;
    if (y < 0) y = canvasRef.current.a.height;
  }, []);

  // Renderizar
  const render = useCallback(() => {
    ctxRef.current.b.save();
    ctxRef.current.b.fillStyle = backgroundColor;
    ctxRef.current.b.fillRect(
      0,
      0,
      canvasRef.current.b.width,
      canvasRef.current.b.height
    );
    ctxRef.current.b.restore();

    ctxRef.current.b.save();
    ctxRef.current.b.filter = "blur(12px)";
    ctxRef.current.b.drawImage(canvasRef.current.a, 0, 0);
    ctxRef.current.b.restore();

    ctxRef.current.b.save();
    ctxRef.current.b.drawImage(canvasRef.current.a, 0, 0);
    ctxRef.current.b.restore();
  }, [backgroundColor]);

  // Añade esta nueva función para calcular densidad
  const calculateDensity = useCallback(() => {
    const canvas = canvasRef.current.a;
    if (!canvas) return;

    const imageData = ctxRef.current.a.getImageData(
      0,
      0,
      canvas.width,
      canvas.height
    );
    const data = imageData.data;
    let opaquePixels = 0;
    let totalPixels = canvas.width * canvas.height;

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 10) {
        // Si el alpha es mayor a 10
        opaquePixels++;
      }
    }

    densityRef.current = opaquePixels / totalPixels;

    // Si la densidad supera el máximo, reiniciar
    if (densityRef.current > maxDensity) {
      resetCanvas();
    }
  }, []);
  const resetCanvas = useCallback(() => {
    // Limpiar ambos canvases
    ctxRef.current.a.clearRect(
      0,
      0,
      canvasRef.current.a.width,
      canvasRef.current.a.height
    );
    ctxRef.current.b.clearRect(
      0,
      0,
      canvasRef.current.b.width,
      canvasRef.current.b.height
    );

    // Reiniciar todas las pipes
    initPipes();
    tickRef.current = 0;
    densityRef.current = 0;
  }, [initPipes]);
  // Bucle de animación
  const draw = useCallback(() => {
    updatePipes();
    render();

    // Verificar densidad periódicamente (cada 2 segundos aprox)
    if (tickRef.current % 120 === 0) {
      calculateDensity();
    }

    animationRef.current = requestAnimationFrame(draw);
  }, [updatePipes, render, calculateDensity]);

  // Configuración inicial
  const setup = useCallback(() => {
    // Crear canvases
    const container = canvasContainerRef.current;
    canvasRef.current = {
      a: document.createElement("canvas"),
      b: document.createElement("canvas"),
    };

    canvasRef.current.b.style = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    `;

    container.appendChild(canvasRef.current.b);
    ctxRef.current = {
      a: canvasRef.current.a.getContext("2d"),
      b: canvasRef.current.b.getContext("2d"),
    };

    centerRef.current = [];
    tickRef.current = 0;

    resize();
    initPipes();
    draw();
  }, [initPipes, draw]);

  // Redimensionar
  const resize = useCallback(() => {
    const { innerWidth, innerHeight } = window;

    if (canvasRef.current.a) {
      canvasRef.current.a.width = innerWidth;
      canvasRef.current.a.height = innerHeight;
      ctxRef.current.a.drawImage(canvasRef.current.b, 0, 0);
    }

    if (canvasRef.current.b) {
      canvasRef.current.b.width = innerWidth;
      canvasRef.current.b.height = innerHeight;
      ctxRef.current.b.drawImage(canvasRef.current.a, 0, 0);
    }

    centerRef.current[0] = 0.5 * innerWidth;
    centerRef.current[1] = 0.5 * innerHeight;
  }, []);

  // Efectos de React
  useEffect(() => {
    setup();

    const handleResize = () => {
      resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [setup, resize]);

  // El componente se inicializa si cambian estos props
  useEffect(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    setup();
  }, [baseHue, backgroundColor, setup]);

  return (
    <div
      ref={canvasContainerRef}
      className="content--canvas"
      style={{
        position: "relative",
        width: "100%",
        height: window.innerWidth <= 768 ? "50%" : "100%",
      }}
    />
  );
};

export default CanvasPersonalizado;
